import React, { useEffect } from "react";
import { openExternal } from "@/lib/openExternal";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Sparkles, Instagram, Mail, MessageCircle, ArrowLeft } from "lucide-react";
import simoneAsset from "@/assets/simone-brusco.jpg.asset.json";
import FounderFooterLink from "@/components/landing/FounderFooterLink";
import BrincadeirasFooterLink from "@/components/landing/BrincadeirasFooterLink";

/**
 * /sobre — Página institucional pública (Sobre a Fundadora / Desenvolvedora)
 * Usada como Marketing URL na App Store e Website do Desenvolvedor na Play Console.
 * Institucional: sem CTAs de pagamento, sem preços.
 */

const SUPPORT_EMAIL = "contato@materna360.com.br";
const WHATSAPP_INTL = "5511925080360";

const Sobre: React.FC = () => {
  useEffect(() => {
    const prevTitle = document.title;
    // Mesmo texto do HTML gerado no build (src/lib/seo/publicRoutes.ts):
    // navegação dentro do app não deve trocar o título por outro.
    document.title = "Simone Brusco — pedagoga e fundadora do Materna360";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") ?? null;
    meta?.setAttribute(
      "content",
      "Quem está por trás do Materna360: Simone Brusco, pedagoga, mãe do Alexandre e do Arthur, pós-graduada em desenvolvimento de aplicativos, fundadora e CEO da plataforma.",
    );
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-dvh bg-[#faf8f6] text-[#1a1a2e]">
      {/* Header simples */}
      <header className="border-b border-black/5 bg-white/70 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm font-medium text-[#55575d] inline-flex items-center gap-1.5 hover:text-[#fd2597] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Materna360
          </Link>
          <span className="text-[11px] uppercase tracking-[0.14em] text-[#999]">
            Sobre a fundadora
          </span>
        </div>
      </header>

      {/* Hero — foto discreta ao lado do título */}
      <section className="max-w-3xl mx-auto px-5 pt-10 pb-8 md:pt-16 md:pb-14">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Header lockup: name + subtitle on the left, small portrait on the right */}
          <div className="flex gap-5 items-start">
            <div className="flex-1 min-w-0">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#fdf2f8] text-[#fd2597] text-[12px] font-semibold uppercase tracking-[0.1em]">
                <Sparkles className="w-3.5 h-3.5" />
                Feito por uma mãe, para mães
              </span>
              {/* Nome e papel no mesmo h1: buscadores precisam do papel no
                  cabeçalho principal, e o desenho segue idêntico. */}
              <h1 className="mt-3">
                <span
                  className="block text-[30px] md:text-[40px] leading-[1.05] font-bold text-[#1a1a2e]"
                  style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                >
                  Simone Brusco
                </span>
                <span className="mt-1 block text-[14px] md:text-[16px] font-normal text-[#55575d] leading-relaxed">
                  Pedagoga, mãe do Alexandre e do Arthur, e fundadora do{" "}
                  <strong className="text-[#1a1a2e]">Materna360</strong>.
                </span>
              </h1>
            </div>

            <div className="flex-shrink-0 w-24 h-30 md:w-28 md:h-34 rounded-[18px] overflow-hidden bg-[#fdf2f8] shadow-[0_10px_30px_-10px_rgba(253,37,151,0.25)]">
              <img
                src={simoneAsset.url}
                alt="Simone Brusco, fundadora do Materna360"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>

          <p className="mt-4 text-[15px] md:text-[17px] text-[#55575d] leading-relaxed">
            Empreendedora há mais de uma década, pensei, desenhei e construí esta plataforma de
            apoio materno do primeiro rascunho a cada palavra do app.
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            {[
              "Pedagoga",
              "Fundadora & CEO",
              "Mãe do Alexandre e Arthur",
              "Pós-graduada em Apps",
            ].map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full bg-white border border-black/5 text-[12px] md:text-[13px] text-[#55575d]"
              >
                {tag}
              </span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Trajetória */}
      <section className="max-w-3xl mx-auto px-5 pb-14">
        <div className="bg-white rounded-[20px] border border-black/5 p-7 md:p-10 shadow-[0_10px_40px_-20px_rgba(26,26,46,0.12)]">
          <h2
            className="text-[24px] md:text-[28px] font-bold text-[#1a1a2e] mb-6"
            style={{ fontFamily: "'Fraunces', Georgia, serif" }}
          >
            Minha trajetória
          </h2>

          <div className="space-y-5 text-[15px] md:text-[16px] text-[#3a3a4a] leading-[1.75]">
            <p>
              Eu tinha 13 anos quando comecei a ajudar minha mãe na escola de educação
              infantil que ela tinha. Não era aluna: eu acompanhava as crianças, organizava
              atividades e aprendia no colo da rotina. Foi ali, entre giz, risadas e livros
              coloridos, que descobri que educar é, de verdade, um superpoder. A{" "}
              <strong>Pedagogia</strong> veio logo depois — não como escolha, mas como destino.
            </p>

            <p>
              A vida, claro, escreveu capítulos que eu não poderia prever. Chegaram meus
              dois maiores mestres: <strong>Alexandre</strong>, e depois <strong>Arthur</strong>.
              Eles não só iluminaram meus dias; eles reescreveram minha noção de amor, sono,
              paciência e propósito. Se tem uma verdade que carrego comigo, em meio a tantos
              papéis — pedagoga, empreendedora, criadora, desenvolvedora — é esta: o que mais
              amo na vida é ser <strong>mãe</strong>. Por mais que minha rotina seja uma dança
              louca entre a Glorya, o Materna, códigos, ideias e reuniões, nada se compara ao
              que sinto quando olho para os meus filhos. Ser mãe é minha raiz e minha maior paixão.
            </p>

            <p>
              Nesse meio tempo, um convite inesperado me apresentou ao marketing. Aos 40
              anos nasceu a <strong>Glorya</strong>, minha agência, que até hoje me desafia
              a criar, inovar e ajudar marcas a se conectarem verdadeiramente com pessoas.
              Foi na Glorya que aprendi a traduzir emoção em estratégia — e a entender que,
              por trás de toda boa comunicação, existe sempre uma história humana.
            </p>

            <p>
              E aí entrou uma descoberta que mudou tudo: a inteligência artificial.
              Fiquei fascinada pela ideia de que era possível construir, com as próprias
              mãos, experiências que tocassem outras pessoas — que linhas de código pudessem
              virar cuidado, escuta e apoio. A menina que se encantara com a educação se
              apaixonou de novo, agora pela linguagem da tecnologia. Foi essa paixão que me
              levou a fazer uma <strong>pós-graduação em desenvolvimento de aplicativos</strong>: eu
              queria entender, de verdade, como transformar tudo o que aprendi em algo que
              estivesse no bolso de uma mãe, a qualquer hora.
            </p>

            <p>
              Hoje, aos 50, sinto que é hora de reunir tudo o que sou — a pedagoga, a mãe,
              a empreendedora, a desenvolvedora — em um só lugar. Assim nasceu o{" "}
              <strong>Materna360</strong>: um espaço onde educação, parentalidade e propósito
              se encontram para inspirar e apoiar mães reais, no dia real delas.
            </p>

            <div className="border-l-2 border-[#fd2597] pl-5 py-1 my-6 bg-[#fdf2f8]/40 rounded-r-lg">
              <p className="text-[15px] md:text-[16px] text-[#1a1a2e] italic leading-relaxed">
                Cada pedacinho do Materna360 foi feito por mim — da concepção da ideia à
                curadoria dos conteúdos, do tom de voz ao desenho de cada tela, das linhas
                de código à resposta do assistente. Não deleguei a alma do projeto: eu
                aprendi a construir para que ele soasse, em cada detalhe, como a minha voz.
              </p>
            </div>

            <p>
              Minha história é um lembrete de que cada fase da vida tem seu valor, suas
              lições, e que nunca é tarde para transformar aquilo que você ama em algo
              que faz diferença. E esse é só o começo de um novo capítulo — que estou
              muito animada para dividir com você.
            </p>

            <p className="text-right text-[#fd2597] font-semibold pt-2" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              — Simone Brusco
            </p>
          </div>
        </div>
      </section>

      {/* O que é o Materna360 */}
      <section className="max-w-3xl mx-auto px-5 pb-14">
        <div className="text-center mb-8">
          <h2
            className="text-[24px] md:text-[28px] font-bold text-[#1a1a2e]"
            style={{ fontFamily: "'Fraunces', Georgia, serif" }}
          >
            O que é o Materna360
          </h2>
          <p className="mt-2 text-[15px] text-[#55575d]">
            Uma assistente pessoal materna. Acolhimento, organização e conteúdo curado
            para o dia real de quem cria filhos.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { title: "Rotina & Rituais", desc: "Estrutura leve para o dia sem pesar." },
            { title: "Momento & Diário", desc: "Espaço para a mãe se registrar sem julgamento." },
            { title: "Assistente Materna", desc: "Conversa quando você precisa — 24h por dia." },
            { title: "Biblioteca curada", desc: "Conteúdo observacional, nunca clínico, para filhos 0–6." },
          ].map((c) => (
            <div
              key={c.title}
              className="bg-white rounded-2xl border border-black/5 p-5"
            >
              <h3 className="text-[15px] font-semibold text-[#1a1a2e] mb-1">{c.title}</h3>
              <p className="text-[13.5px] text-[#55575d] leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contato */}
      <section className="max-w-3xl mx-auto px-5 pb-14">
        <div className="bg-gradient-to-br from-[#fdf2f8] to-white rounded-[20px] border border-[#fce7f3] p-7 md:p-9 text-center">
          <Heart className="w-6 h-6 text-[#fd2597] mx-auto mb-3" />
          <h2
            className="text-[22px] md:text-[26px] font-bold text-[#1a1a2e] mb-2"
            style={{ fontFamily: "'Fraunces', Georgia, serif" }}
          >
            Vamos conversar
          </h2>
          <p className="text-[14.5px] text-[#55575d] mb-6 max-w-lg mx-auto">
            Dúvidas, parcerias, imprensa ou só um oi carinhoso — estou por aqui.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#fd2597] text-white text-[14px] font-semibold hover:bg-[#e21f87] transition-colors"
            >
              <Mail className="w-4 h-4" />
              {SUPPORT_EMAIL}
            </a>
            <a
              href={`https://wa.me/${WHATSAPP_INTL}`}
              onClick={(e) => { e.preventDefault(); openExternal(`https://wa.me/${WHATSAPP_INTL}`); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-black/10 text-[#1a1a2e] text-[14px] font-semibold hover:border-[#fd2597] hover:text-[#fd2597] transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <a
              href="https://instagram.com/materna360.app"
              onClick={(e) => { e.preventDefault(); openExternal("https://instagram.com/materna360.app"); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-black/10 text-[#1a1a2e] text-[14px] font-semibold hover:border-[#fd2597] hover:text-[#fd2597] transition-colors"
            >
              <Instagram className="w-4 h-4" />
              @materna360.app
            </a>
          </div>
        </div>
      </section>

      {/* Footer legal */}
      <footer className="border-t border-black/5 bg-white/60">
        <div className="max-w-3xl mx-auto px-5 py-8 text-center space-y-3">
          <p className="text-[12px] text-[#888] leading-relaxed">
            <strong className="text-[#55575d]">Materna360</strong> é operado por
            SummitHub Soluções Empresariais Ltda
            <br />
            CNPJ 58.438.716/0001-55
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12px] text-[#55575d]">
            <FounderFooterLink className="hover:text-[#fd2597]" />
            <BrincadeirasFooterLink className="hover:text-[#fd2597]" />
            <Link to="/politica-privacidade" className="hover:text-[#fd2597]">
              Política de Privacidade
            </Link>
            <Link to="/termos-uso" className="hover:text-[#fd2597]">
              Termos de Uso
            </Link>
            <Link to="/politica-cookies" className="hover:text-[#fd2597]">
              Política de Cookies
            </Link>
          </div>
          <p className="text-[11px] text-[#999] pt-2">
            © {new Date().getFullYear()} Materna360. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Sobre;
